import { getClauseCampaigns } from '@data-access';

export type AssignmentClause = 'gp' | 'new';

export type AssignmentMapVariant = 'admin' | 'team_assignment';

export type AssignmentMapColorMode = 'sampler' | 'organic' | 'period' | 'sampling' | 'laboratory';

export type DrawActionMode =
  | 'sms'
  | 'sampler'
  | 'assign_laboratory'
  | 'make_visible'
  | 'reassign_sampler'
  | 'confirm_laboratory';

export interface AssignmentMapCapabilities {
  variant: AssignmentMapVariant;
  drawModes: DrawActionMode[];
  colorModes: AssignmentMapColorMode[];
  samplerLegendMode: 'organization' | 'user';
  showReferenceDate: boolean;
  showRecentActions: boolean;
  showSmsActions: boolean;
  showSamplerOrganizationSelect: boolean;
  filterOrganizations: boolean;
  filterOsOrganizations: boolean;
  filterSamplers: boolean;
  filterLaboratories: boolean;
  filterOrganic: boolean;
  filterAssignmentState: boolean;
  filterAnalysisTypes: boolean;
  allowClearAssignment: boolean;
  smsReadOnly: boolean;
}

export interface AssignmentQueryParams {
  clause: AssignmentClause;
  campaignYear?: number;
}

export function resolveNgpCampaignYear(): number {
  const clauses = getClauseCampaigns();
  for (const raw of clauses) {
    const match = /^new-(\d{4})$/.exec(raw);
    if (match) {
      return Number(match[1]);
    }
  }
  return new Date().getFullYear();
}

export type AssignmentSearchParams =
  | { clause: 'gp' }
  | { clause: 'new'; campaign_year: number };

export function assignmentSearchParams(
  params: AssignmentQueryParams,
): AssignmentSearchParams {
  if (params.clause === 'new') {
    return {
      clause: 'new',
      campaign_year: params.campaignYear ?? resolveNgpCampaignYear(),
    };
  }
  return { clause: 'gp' };
}

export interface AssignmentMapConfig {
  clause: AssignmentClause;
  campaignYear?: number;
  i18nPrefix: 'gpAssignment' | 'ngpAssignment';
  queryKey: string;
  capabilities: AssignmentMapCapabilities;
}

const ADMIN_NGP_CAPABILITIES: AssignmentMapCapabilities = {
  variant: 'admin',
  drawModes: ['sms', 'sampler', 'assign_laboratory', 'make_visible', 'reassign_sampler', 'confirm_laboratory'],
  colorModes: ['sampler', 'organic', 'period', 'sampling', 'laboratory'],
  // Même clé que /os/ngp-sampling-assignment : couleur stable par préleveur.
  samplerLegendMode: 'user',
  showReferenceDate: true,
  showRecentActions: true,
  showSmsActions: true,
  showSamplerOrganizationSelect: true,
  filterOrganizations: true,
  filterOsOrganizations: true,
  filterSamplers: true,
  filterLaboratories: true,
  filterOrganic: true,
  filterAssignmentState: true,
  filterAnalysisTypes: true,
  allowClearAssignment: true,
  smsReadOnly: false,
};

const TEAM_ASSIGNMENT_CAPABILITIES: AssignmentMapCapabilities = {
  variant: 'team_assignment',
  drawModes: ['reassign_sampler'],
  colorModes: ['sampler', 'organic', 'sampling'],
  samplerLegendMode: 'user',
  showReferenceDate: false,
  showRecentActions: false,
  showSmsActions: false,
  showSamplerOrganizationSelect: false,
  filterOrganizations: false,
  filterOsOrganizations: false,
  filterSamplers: true,
  filterLaboratories: false,
  filterOrganic: true,
  filterAssignmentState: true,
  filterAnalysisTypes: false,
  allowClearAssignment: false,
  smsReadOnly: true,
};

export const GP_ASSIGNMENT_CONFIG: AssignmentMapConfig = {
  clause: 'gp',
  i18nPrefix: 'gpAssignment',
  queryKey: 'gp-sampling-assignment',
  capabilities: {
    variant: 'admin',
    drawModes: ['sms', 'sampler', 'confirm_laboratory'],
    colorModes: ['sampler', 'organic', 'period'],
    samplerLegendMode: 'user',
    showReferenceDate: false,
    showRecentActions: true,
    showSmsActions: true,
    showSamplerOrganizationSelect: false,
    filterOrganizations: true,
    filterOsOrganizations: true,
    filterSamplers: true,
    filterLaboratories: false,
    filterOrganic: true,
    filterAssignmentState: true,
    filterAnalysisTypes: false,
    allowClearAssignment: false,
    smsReadOnly: false,
  },
};

export function ngpAssignmentConfig(campaignYear?: number): AssignmentMapConfig {
  return {
    clause: 'new',
    campaignYear: campaignYear ?? resolveNgpCampaignYear(),
    i18nPrefix: 'ngpAssignment',
    queryKey: 'ngp-sampling-assignment',
    capabilities: ADMIN_NGP_CAPABILITIES,
  };
}

export function ngpTeamAssignmentConfig(campaignYear?: number): AssignmentMapConfig {
  return {
    clause: 'new',
    campaignYear: campaignYear ?? resolveNgpCampaignYear(),
    i18nPrefix: 'ngpAssignment',
    queryKey: 'ngp-team-assignment',
    capabilities: TEAM_ASSIGNMENT_CAPABILITIES,
  };
}

export function isTeamAssignmentVariant(
  value: AssignmentMapConfig | AssignmentMapCapabilities,
): boolean {
  const capabilities = 'capabilities' in value ? value.capabilities : value;
  return capabilities.variant === 'team_assignment';
}
